import { useState, useEffect, useCallback } from "react";
import "./css/admincust.css";
import CustInfoModal from "./modal/CustInfoModal";
import AdminWithdrawalCustInfo from "./modal/AdminWithdrawalCustInfo";
import { getCusts, getCustDetail, updateCust, deleteCusts, searchCusts, getWithdrawalCusts, getWithdrawalCustDetail } from "../../utils/adminCustService";
import { downloadCustExcel } from "../../utils/adminExcelService";
import { formatDate } from "../../utils/commonService";
import Pagination from "../common/Pagination";

const AdminCust = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [sortColumn, setSortColumn] = useState("custSignInDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const [activeTab, setActiveTab] = useState("ACTIVE");
  const [searchType, setSearchType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loginType, setLoginType] = useState("all");
  const [isSearching, setIsSearching] = useState(false);
  
  const [excelLoading, setExcelLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // 고객 목록 조회 (탭 기반)
  const fetchCustomers = useCallback(async (pageNum = 0, col = 'custSignInDate', dir = 'desc', tab = "ACTIVE") => {
    try {
      setLoading(true);
      setError(null);
      setIsSearching(false);

      const data = tab === "WITHDRAWAL"
        ? await getWithdrawalCusts(pageNum, pageSize, col, dir)
        : await getCusts(pageNum, pageSize, col, dir);

      setCustomers(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
      setCurrentPage(pageNum);
    } catch (err) {
      setError(err.message || "고객 목록을 불러오는데 실패했습니다.");
      console.error("고객 목록 조회 오류:", err);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchCustomers(0, 'custSignInDate', 'desc', "ACTIVE");
  }, [fetchCustomers]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setSearchTerm("");
    setSearchType("all");
    setLoginType("all");
    setSelectedIds(new Set());
    fetchCustomers(0, sortColumn, sortDirection, tab);
  };

  const handleDetailClick = async (customer) => {
    try {
      setLoading(true);
      const detailData = activeTab === "WITHDRAWAL"
        ? await getWithdrawalCustDetail(customer.custCode)
        : await getCustDetail(customer.custCode);
      setSelectedCustomer(detailData);
      setIsModalOpen(true);
    } catch (err) {
      setError(err.message);
      console.error("고객 상세 조회 오류:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleUpdateCustomer = async (updatedData) => {
    try {
      setLoading(true);
      
      // 필수 필드 검증
      if (!updatedData.custGender) {
        alert("성별이 올바르지 않습니다.");
        setLoading(false);
        return;
      }

      const modRequest = {
        custNm: updatedData.custNm || "",
        custGender: updatedData.custGender,
        custBirthYear: updatedData.custBirthYear || "",
        custTelNo: updatedData.custTelNo || "",
        custState: updatedData.custState || "ACTIVE",
        custMenuAuth: updatedData.custMenuAuth || "",
      };
            
      await updateCust(selectedCustomer.custCode, modRequest);
      setIsModalOpen(false);
      setSelectedCustomer(null);
      await fetchCustomers(currentPage);
      alert("고객 정보가 수정되었습니다.");
    } catch (err) {
      setError(err.message);
      alert("고객 정보 수정 실패: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = (custCode) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(custCode)) {
      newSelectedIds.delete(custCode);
    } else {
      newSelectedIds.add(custCode);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(customers.map(c => c.custCode));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleDeleteCustomers = async () => {
    if (selectedIds.size === 0) {
      alert("삭제할 고객을 선택해주세요.");
      return;
    }

    if (!window.confirm(`선택된 ${selectedIds.size}명의 고객을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteCusts(Array.from(selectedIds));
      setSelectedIds(new Set());
      await fetchCustomers(0);
      alert("고객이 삭제되었습니다.");
    } catch (err) {
      setError(err.message);
      alert("고객 삭제 실패: " + err.message);
      console.error("고객 삭제 오류:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (page = 0, col = sortColumn, dir = sortDirection, filterOverrides = {}) => {
    try {
      setLoading(true);
      setError(null);
      setIsSearching(true);

      const effectiveLoginType = filterOverrides.loginType !== undefined ? filterOverrides.loginType : loginType;

      const searchParams = {
        custState: activeTab,
        loginType: effectiveLoginType === "all" ? null : effectiveLoginType,
        searchType: searchType === "all" ? "all" : searchType,
        searchTerm: searchTerm.trim(),
      };

      const data = await searchCusts(searchParams, page, pageSize, col, dir);
      setCustomers(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
      setCurrentPage(data.number);
    } catch (err) {
      setError(err.message);
      alert("검색 실패: " + err.message);
      console.error("검색 오류:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    isSearching ? handleSearch(page, sortColumn, sortDirection) : fetchCustomers(page, sortColumn, sortDirection, activeTab);
  };

  const handleSort = (column) => {
    const newDir = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDir);
    isSearching ? handleSearch(0, column, newDir) : fetchCustomers(0, column, newDir, activeTab);
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    const result = await downloadCustExcel();
    if (!result.success) {
      alert(result.error || "엑셀 다운로드에 실패했습니다.");
    }
    setExcelLoading(false);
  };

  const handleResetSearch = () => {
    setSearchTerm("");
    setSearchType("all");
    setLoginType("all");
    setSelectedIds(new Set());
    fetchCustomers(0, sortColumn, sortDirection, activeTab);
  };



  return (
    <div className="admincust-container">
      <div className="admincust-wrap">
        {error && <div className="admincust-error-message">{error}</div>}

        <div className="admincust-tab-navigation">
          <button
            className={`admincust-tab-btn ${activeTab === "ACTIVE" ? "admincust-tab-active" : ""}`}
            onClick={() => handleTabChange("ACTIVE")}
          >
            활성
          </button>
          <button
            className={`admincust-tab-btn ${activeTab === "WITHDRAWAL" ? "admincust-tab-active" : ""}`}
            onClick={() => handleTabChange("WITHDRAWAL")}
          >
            탈퇴 요청
          </button>
        </div>

        {activeTab === "ACTIVE" && <div className="admincust-filter-wrap">
          <div className="admincust-filter-row">
            <div className="admincust-filter-group">
              <label htmlFor="login-type">로그인 타입</label>
              <select
                name="login-type"
                id="login-type"
                className="admincust-select"
                value={loginType}
                onChange={(e) => {
                  const val = e.target.value;
                  setLoginType(val);
                  handleSearch(0, sortColumn, sortDirection, { loginType: val });
                }}
              >
                <option value="all">전체</option>
                <option value="DNBN">동네방네</option>                
                <option value="SOCIAL">소셜</option>
                {/* <option value="NAVER">네이버</option> */}
              </select>
            </div>
          </div>

          <div className="admincust-filter-row admincust-search-row">
            <div className="admincust-search-group">
              <select
                name="search-type"
                id="search-type"
                className="admincust-select-type"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="all">전체</option>
                <option value="custcode">사용자코드</option>
                <option value="custnm">이름</option>
                <option value="custtelno">연락처</option>
              </select>
              <input
                type="text"
                className="admincust-input"
                placeholder="검색어를 입력하세요"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="admincust-search-btn" onClick={handleSearch}>검색</button>
              <button className="admincust-search-btn admincust-search-reset" onClick={handleResetSearch}>
                초기화
              </button>
            </div>
          </div>
        </div>}

        <div className="admincust-table-wrap">
          <div className="admincust-table-header">
            <div className="admincust-table-info">
              총 <span className="admincust-count">{totalElements}</span>건
            </div>
            <div className="admincust-table-actions">
              {activeTab === "ACTIVE" && (
                <button
                  className="admincust-btn admincust-btn-excel"
                  onClick={handleExcelDownload}
                  disabled={excelLoading}
                >
                  {excelLoading ? "다운로드 중..." : "엑셀 다운로드"}
                </button>
              )}
              {selectedIds.size > 0 && (
                <button className="admincust-btn admincust-btn-delete" onClick={handleDeleteCustomers}>
                  삭제 ({selectedIds.size})
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="admincust-loading">로딩 중...</div>
          ) : (
            <table className="admincust-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === customers.length && customers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {activeTab === "WITHDRAWAL" ? (
                    ["사용자코드", "이름", "연락처", "로그인 타입", "탈퇴 요청일"].map((label) => (
                      <th key={label}>{label}</th>
                    ))
                  ) : (
                    [
                      { label: "사용자코드", col: "custCode" },
                      { label: "이름", col: "custNm" },
                      { label: "연락처", col: "custTelNo" },
                      { label: "로그인 타입", col: "custLoginType" },
                      { label: "가입일", col: "custSignInDate" },
                      { label: "상태", col: "custState" },
                    ].map(({ label, col }) => (
                      <th
                        key={col}
                        className={`admincust-sortable${sortColumn === col ? " admincust-sort-active" : ""}`}
                        onClick={() => handleSort(col)}
                      >
                        {label}
                        <span className="admincust-sort-icon">
                          {sortColumn === col ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                        </span>
                      </th>
                    ))
                  )}
                  <th>상세</th>
                </tr>
              </thead>
              <tbody>
                {customers.length > 0 ? (
                  customers.map((customer) => (
                    <tr key={customer.custCode}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(customer.custCode)}
                          onChange={() => handleSelectCustomer(customer.custCode)}
                        />
                      </td>
                      <td>{customer.custCode}</td>
                      <td>{customer.custNm}</td>
                      <td>{customer.custTelNo}</td>
                      <td>{customer.custLoginType === "DNBN" ? "동네방네" : customer.custLoginType === "SOCIAL" ? "소셜" : "-"}</td>
                      {activeTab !== "WITHDRAWAL" && <td>{formatDate(customer.custSignInDate)}</td>}
                      {activeTab === "WITHDRAWAL" ? (
                        <>
                          <td>{formatDate(customer.custWithDrawalRequestTime)}</td>
                        </>
                      ) : (
                        <td className={
                          customer.custState === "활성" ? "admincust-status-active"
                            : customer.custState === "제재" ? "admincust-status-suspended"
                              : customer.custState === "탈퇴" ? "admincust-status-withdrawal"
                                : ""
                        }>
                          {customer.custState === "활성" ? "활성"
                            : customer.custState === "제재" ? "정지"
                              : customer.custState === "탈퇴" ? "탈퇴"
                                : customer.custState}
                        </td>
                      )}
                      <td>
                        <button
                          className="admincust-btn admincust-btn-detail"
                          onClick={() => handleDetailClick(customer)}
                        >
                          보기
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="admincust-no-data">
                      고객 정보가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {isModalOpen && selectedCustomer && (
        activeTab === "WITHDRAWAL" ? (
          <AdminWithdrawalCustInfo
            customerData={selectedCustomer}
            onClose={handleCloseModal}
          />
        ) : (
          <CustInfoModal
            customerData={selectedCustomer}
            onClose={handleCloseModal}
            onUpdate={handleUpdateCustomer}
          />
        )
      )}
    </div>
  );
};

export default AdminCust;